/**
 * Shopper-facing copy for order status. ONE map, every shopper surface.
 *
 * `orders.status` is internal vocabulary and must never reach a customer's
 * screen. It did: the order page rendered "Status: awaiting_batch_charge",
 * and the orders LIST rendered the same raw column. Two surfaces, one defect
 * -- so this lives beside `orderListHelpers.js` and both import it rather
 * than each carrying a mapping that can drift.
 *
 * THE COPY DEPENDS ON TWO AXES, NOT ONE
 * =====================================
 * `orders.status` alone cannot say what is true for the shopper, because
 * `payment_state` carries the failure axis. A declined card leaves the status
 * at `awaiting_batch_charge` while `payment_state` reads `charge_failed`, and
 * the shopper needs to hear about their card, not about our scheduling. So
 * `payment_state` OVERRIDES -- but only when it names a real problem, never
 * for the routine `pending_capture`/`charged` values, or every un-charged
 * order would read as an error.
 *
 * "PROCESSING" MEANS CHARGED, NOT "IN PRODUCTION"
 * ==============================================
 * This is the one a future reader is most likely to "correct" into something
 * wrong. `processing` is set by `services/batch_charge.py` (CHARGED_STATUS)
 * at the MOMENT THE CARD SUCCEEDS -- it is a payment fact, not a
 * manufacturing one. Charging is what release does, and release is what
 * hands the order to the decorator, so "Order sent to decorator" is true
 * here. It does NOT mean the decorator has started work; we cannot observe
 * that on this surface (see below).
 *
 * WHY "Order recorded" AND NOT "Payment received"
 * ==============================================
 * Batches charge at RELEASE, not at close (Jake, 2026-08-27), so an order
 * legitimately sits in `awaiting_batch_charge` from checkout until its batch
 * releases -- hours to DAYS. Claiming payment during that window would be
 * false for the entire window. "Order recorded" is the strongest TRUE claim.
 *
 * "Sent to the decorator" is LOCKED design vocabulary, not a coinage: see
 * `pages/distributor/store-editor/StoreBatches/batchStatus.js`, where
 * `released.gloss` is exactly that string.
 *
 * NO "BATCH" IN ANY STRING
 * ========================
 * Jake: "the shopper probably has no idea what a batch is." A batch is our
 * scheduling concept. Pinned by a test over the whole map.
 *
 * DECORATOR-SIDE STATES ARRIVE AS OUR OWN STATUS
 * ==============================================
 * After hand-off the back office owns order status (Jake, 2026-10-22). Its
 * fulfillment webhook (`api/src/api/internal/deconetwork_fulfillment.py`)
 * moves `orders.status` forward through ONE mapping
 * (`api/src/services/backoffice_order_status.py`): their production keys ->
 * `in_production`, their shipped key -> `shipped`. Those are therefore values
 * this page observes, and they have copy below. Their own raw key is never
 * rendered (their taxonomy is EDITABLE PER WORKSPACE), only our status.
 *
 * Their cancel key sets `decorator_cancelled`, which is OWNER-ONLY: the API
 * masks it to `processing` for every buyer, so this page never receives it.
 */

/**
 * The safe generic. An unmapped or missing status degrades to the weakest
 * true statement -- we know an order exists, because we are rendering it.
 * Echoing the unknown value instead is the original defect re-entering
 * through the default branch.
 */
export const FALLBACK_STATUS_COPY = "Order recorded";

/**
 * `orders.status` -> shopper copy.
 *
 * Keys are the values the backend actually WRITES, derived by grepping
 * assignments across `api/src` rather than trusting one constants block:
 *
 *   payment_pending        models/account.py:3787 -- checkout never finished
 *   awaiting_batch_charge  models/order.py:53 -- vaulted, not yet charged
 *   processing             services/batch_charge.py CHARGED_STATUS -- CHARGED
 *   canceled               services/pending_order_sweep.py:295,
 *                          services/batch_order_edit.py:745
 *   in_production          services/backoffice_order_status.py -- the
 *                          back office reports the order in production
 *   shipped                services/backoffice_order_status.py -- the
 *                          back office reports it shipped
 *
 * Note `canceled` is single-l: that is the spelling the backend writes. The
 * DISPLAY is "Cancelled" independently of how the column is spelled.
 *
 * `payment_pending` maps to the generic rather than to something like
 * "Incomplete": a shopper reaching this page has an order to look at, and
 * that status normally means an abandoned checkout the sweep will reclaim.
 */
export const SHOPPER_ORDER_STATUS = Object.freeze({
  payment_pending: FALLBACK_STATUS_COPY,
  awaiting_batch_charge: "Order recorded",
  processing: "Order sent to decorator",
  in_production: "In production",
  shipped: "Shipped",
  canceled: "Cancelled",
  // The DECORATOR cancelled (Jake): OWNER-ONLY. The backend never serves
  // this value to a buyer -- it masks it to `processing`
  // (`api/src/services/order_status.py`, `buyer_visible_status`). Mapped
  // anyway, to the processing copy, so a masking regression still cannot
  // put the word "cancelled" (or the raw token) on a shopper's screen.
  decorator_cancelled: "Order sent to decorator",
});

/**
 * `orders.payment_state` values that OVERRIDE the status-derived copy.
 *
 * Deliberately narrow. `pending_capture` and `charged` are the routine path
 * and must fall through to the status map -- treating them as overrides
 * would make every un-charged order read as a problem.
 *
 * `canceled` appears here as well as in the status map because the two
 * columns are written TOGETHER (`batch_order_edit.py:745` sets
 * `status = 'canceled', payment_state = 'canceled'`) and a row that somehow
 * carries only the payment-state half must still read as cancelled rather
 * than falling through to "Order recorded" -- which would tell a shopper
 * their cancelled order was live.
 *
 * The copy deliberately does NOT name the card ("please update your card").
 * Whether the storefront should prompt a shopper to fix payment, or leave
 * that to the distributor who owns the batch, is a product decision Jake
 * has not made -- so this states the fact and stops.
 */
export const SHOPPER_PAYMENT_STATE = Object.freeze({
  charge_failed: "Payment problem",
  canceled: "Cancelled",
});

/**
 * Resolve the copy a shopper should see for one order.
 *
 * @param {string|null|undefined} status `orders.status`
 * @param {string|null|undefined} paymentState `orders.payment_state`
 * @returns {string} Copy safe to render. Never a raw enum, never empty.
 */
export function shopperOrderStatus(status, paymentState) {
  if (typeof paymentState === "string") {
    const override = SHOPPER_PAYMENT_STATE[paymentState.trim().toLowerCase()];
    if (override) return override;
  }
  if (typeof status !== "string") return FALLBACK_STATUS_COPY;
  return SHOPPER_ORDER_STATUS[status.trim().toLowerCase()] || FALLBACK_STATUS_COPY;
}
